"use client";

import { useId } from "react";
import { Typography } from "./Typography";
import { cn } from "@/lib/utils/cn";

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string = string> {
  options: readonly [SegmentedControlOption<T>, SegmentedControlOption<T>, ...SegmentedControlOption<T>[]];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  disabled?: boolean;
  className?: string;
  indicatorClassName?: string;
  optionClassName?: string;
  "aria-label"?: string;
}

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  size = "md",
  disabled = false,
  className,
  indicatorClassName,
  optionClassName,
  "aria-label": ariaLabel,
}: SegmentedControlProps<T>) {
  const id = useId();
  const selectedIndex = options.findIndex((o) => o.value === value);
  const count = options.length;

  const pad = size === "sm" ? 2 : 4;
  const itemPadding = size === "sm" ? "px-3 py-1" : "px-4 py-1.5";

  const leftPercent = (selectedIndex * 100) / count;
  const leftPx = (pad * (count - 2 * selectedIndex)) / count;
  const widthPercent = 100 / count;
  const widthPx = (2 * pad) / count;

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        "relative inline-grid h-[40px] shrink-0 rounded-lg border border-gray-200 bg-gray-100",
        className,
      )}
      style={{
        padding: `${pad}px`,
        gridTemplateColumns: `repeat(${count}, 1fr)`,
      }}
    >
      {/* Sliding indicator */}
      <div
        className={cn(
          "absolute rounded-md border border-gray-200 bg-white",
          indicatorClassName,
        )}
        style={{
          top: `${pad}px`,
          bottom: `${pad}px`,
          left: `calc(${leftPercent}% + ${leftPx}px)`,
          width: `calc(${widthPercent}% - ${widthPx}px)`,
          transition: "left 200ms ease-in-out",
        }}
      />

      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            id={`${id}-${option.value}`}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => {
              if (!isSelected) onChange(option.value);
            }}
            className={cn(
              "relative z-10", itemPadding, "rounded-md cursor-pointer text-center",
              "transition-colors duration-200",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              optionClassName,
            )}
          >
            <Typography
              as="div"
              variant={size === "sm" ? "small" : "body"}
              weight="medium"
              color={isSelected ? "text-navigator-text-primary" : "text-navigator-text-light"}
            >
              {option.label}
            </Typography>
          </button>
        );
      })}
    </div>
  );
}
